if (!customElements.get('quick-add-modal')) {
	customElements.define('quick-add-modal', class QuickAddModal extends ModalDialog {
		constructor() {
			super();
			this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');
            this.querySelector('[id^="ModalClose-"]')?.removeAttribute('tabindex');
            // ModalDialog traps focus on the dialog itself; it must be focusable.
            this.querySelector('[role="dialog"]')?.setAttribute('tabindex', '-1');
            // Keep announcement content, but inside the one bounded scroll region.
            const announcement = this.querySelector('.quick-add-modal__announcement-bar');
            const scrollRegion = this.querySelector('.quick-add-modal__content-wrapper');
            if (announcement && /^Free Shipping on Selected Orders!\s*[\^†]*$/i.test(announcement.textContent.replace(/\s+/g, ' ').trim())) {
                announcement.remove();
            } else if (announcement && scrollRegion) scrollRegion.append(announcement);
			this.addEventListener('keyup', (event) => {
				if (event.key === 'Escape') event.stopPropagation();
			});
		}

		hide(preventFocus = false) {
            this.initialFocusCleanup?.();
            this.cleanupImportedDialogs();
			this.requestController?.abort();
			this.requestController = null;
			this.mediaSlider?.destroy(true, true);
			this.mediaSlider = null;
			const search = this.searchOverlay;
			const returnToSearch = search && search.isConnected && search.classList.contains('active') && !preventFocus;
			if (search) {
				search.inert = this.searchWasInert;
				if (this.searchAriaHidden === null) search.removeAttribute('aria-hidden');
				else search.setAttribute('aria-hidden', this.searchAriaHidden);
				if (preventFocus) search.classList.remove('active');
				this.searchOverlay = null;
			}
			const cartDrawer = document.querySelector('cart-drawer');
			if (cartDrawer) cartDrawer.setActiveElement(this.openedBy);
			this.modalContent.innerHTML = '';

			if (preventFocus) this.openedBy = null;
			super.hide();
			if (returnToSearch) {
				const focusTarget = this.openedBy?.isConnected ? this.openedBy : search.querySelector('.search__input');
				if (focusTarget) trapFocus(search, focusTarget);
				document.body.classList.add('overflow-hidden');
				// Existing close-button handlers run later in the same click.
				queueMicrotask(() => {
					if (search.classList.contains('active')) document.documentElement.classList.add('overflow-hidden');
				});
			} else if (!document.querySelector('quick-add-modal[open]')) {
				document.documentElement.classList.remove('overflow-hidden');
			}
		}

		show(opener) {
			if (opener.getAttribute('aria-disabled') === 'true') return;
            this.initialFocusCleanup?.();
            this.cleanupImportedDialogs();
			this.requestController?.abort();
			const controller = this.requestController = new AbortController();
            opener.stellarQuickAddRequest = controller;
            this.classList.add('stellar-compact-quick-add');
			const timeout = setTimeout(() => controller.abort(), 20000);
			const search = opener.closest('#search-modal');
			const searchRevision = search?.dataset.overlayRevision;
			opener.setAttribute('aria-disabled', true);
			opener.classList.add('loading');

			if (opener.querySelector('.loading-overlay__spinner')) {
				opener.querySelector('.loading-overlay__spinner').classList.remove('hidden');
			}

			fetch(opener.getAttribute('data-product-url'), { signal: controller.signal })
				.then((response) => {
					if (!response.ok) throw new Error('Product could not be loaded');
					return response.text();
				})
				.then((responseText) => {
					if (search && (!search.isConnected || !search.classList.contains('active') || search.dataset.overlayRevision !== searchRevision || !opener.isConnected)) return;
					const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
					this.productElement = responseHTML.querySelector('section[id^="MainProduct-"]');
                    this.productLanguage = responseHTML.documentElement.lang || document.documentElement.lang || 'en';
					if (this.requestController !== controller) return;
					if (!this.productElement?.querySelector('.product__info-container')) throw new Error('Product content unavailable');
					this.classList.add('stellar-compact-quick-add');
					this.preventDuplicatedIDs();
					this.removeDOMElements();
					this.setInnerHTML(this.modalContent, this.productElement.innerHTML, opener);
                    this.classList.toggle('stellar-quick-add-trade', !!this.modalContent.querySelector('.b2b-product-form, .b2b-pdp-table-wrapper'));
                    this.querySelector('.quick-add-modal__content-wrapper').scrollTop = 0;

					if (window.Shopify && Shopify.PaymentButton) {
						Shopify.PaymentButton.init();
					}

					if (window.ProductModel) window.ProductModel.loadShopifyXR();

					this.removeGalleryListSemantic();
					this.updateImageSizes();
					this.preventVariantURLSwitching();
					this.initMediaSlider();
					if (search) {
						this.searchOverlay = search;
						this.searchWasInert = search.inert;
						this.searchAriaHidden = search.getAttribute('aria-hidden');
						search.inert = true;
					}
					super.show(opener);
                    this.focusInitialControl(opener);
					if (search) search.setAttribute('aria-hidden', 'true');
				})
                .catch(() => {
                    if (this.requestController !== controller || !opener.isConnected) return;
                    if (search && (!search.classList.contains('active') || search.dataset.overlayRevision !== searchRevision)) return;
                    // Keep a recoverable, keyboard-accessible failure instead of a locked page.
                    this.modalContent.replaceChildren();
                    const message = document.createElement('p');
                    message.className = 'stellar-quick-add-error';
                    message.setAttribute('role', 'alert');
                    message.textContent = 'Unable to load this product. Please try again or view the full product details.';
                    const link = document.createElement('a');
                    link.href = opener.getAttribute('data-product-url');
                    link.className = 'button button--secondary';
                    link.textContent = window.theme?.quickviewMore || 'View product details';
                    this.modalContent.append(message, link);
                    if (search) {
                        this.searchOverlay = search;
                        this.searchWasInert = search.inert;
                        this.searchAriaHidden = search.getAttribute('aria-hidden');
                        search.inert = true;
                    }
                    super.show(opener);
                    this.focusInitialControl(opener);
                    if (search) search.setAttribute('aria-hidden', 'true');
                })
                .finally(() => {
                    clearTimeout(timeout);
                    if (opener.stellarQuickAddRequest !== controller) return;
                    delete opener.stellarQuickAddRequest;
                    opener.removeAttribute('aria-disabled');
                    opener.classList.remove('loading');
                    opener.querySelector('.loading-overlay__spinner')?.classList.add('hidden');
                });
        }

        focusInitialControl(opener) {
            this.initialFocusCleanup?.();
            const request = this.requestController;
            let frame;
            const cleanup = () => {
                cancelAnimationFrame(frame);
                this.removeEventListener('transitionend', focus);
                if (this.initialFocusCleanup === cleanup) this.initialFocusCleanup = null;
            };
            const focus = () => {
                if (!this.isConnected || !this.hasAttribute('open') || request !== this.requestController) return cleanup();
                const active = document.activeElement;
                // Never override a user who has already focused this dialog or moved elsewhere.
                if (this.contains(active) || (active !== opener && active !== document.body)) return cleanup();
                this.querySelector('[id^="ModalClose-"]')?.focus({ preventScroll: true });
                if (this.contains(document.activeElement)) cleanup();
            };
            this.initialFocusCleanup = cleanup;
            this.addEventListener('transitionend', focus);
            // Native trap is already installed; wait until the open state has painted.
            frame = requestAnimationFrame(() => { frame = requestAnimationFrame(focus); });
        }

        initMediaSlider() {
            const gallery = this.modalContent.querySelector('.js-media-list');
            if (!gallery) return;
            // Keep native data-media-id nodes: variant selection still prepends its image.
            // Quick add is a small image stack, not another carousel.
            if (gallery.swiper) gallery.swiper.destroy(true, true);
            gallery.setAttribute('data-stellar-quick-gallery', '');
        }

		setInnerHTML(element, html, opener) {
			element.innerHTML = html;
            this.bindImportedDialogs();

			// Reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
            [element, ...(this.importedDialogs || [])].flatMap(root => Array.from(root.querySelectorAll('script'))).forEach(oldScriptTag => {
                if (oldScriptTag.type && !['text/javascript', 'application/javascript', 'module'].includes(oldScriptTag.type)) return;
                // Existing external theme modules already own their global listeners.
                if (oldScriptTag.src && Array.from(document.scripts).some(script => script !== oldScriptTag && !this.contains(script) && !(this.importedDialogs || []).some(modal => modal.contains(script)) && script.src === oldScriptTag.src)) return;
				const newScriptTag = document.createElement('script');
				Array.from(oldScriptTag.attributes).forEach(attribute => {
					newScriptTag.setAttribute(attribute.name, attribute.value)
				});
				newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
				oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
			});

			// Read more button
			const moreBtn = document.createElement('a');
			moreBtn.textContent = `${theme.quickviewMore}`;
			moreBtn.setAttribute('href', opener.getAttribute('data-product-url'));
			moreBtn.setAttribute('class', 'product__read-more button button--simple');
			moreBtn.innerHTML += `<span class="button-icon">
					<svg
						class="icon icon-button-arrow"
						width="14"
						height="12"
						viewBox="0 0 14 12"
						fill="none"
						xmlns="http://www.w3.org/2000/svg"
					>
						<path d="M7.97218 1L12.8333 6M12.8333 6L7.97218 11M12.8333 6L1.16663 6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
					</svg>

					</span>`;
            const paymentMethods = element.querySelector('.stellar-payment-methods');
            if (paymentMethods?.querySelector('img.product_blog-image')) {
                paymentMethods.classList.add('stellar-quick-payment-row');
                paymentMethods.append(moreBtn);
            } else element.querySelector('.product__info-container').appendChild(moreBtn);
		}

		removeDOMElements() {
			const pickupAvailability = this.productElement.querySelector('pickup-availability');
			if (pickupAvailability) pickupAvailability.remove();

			const productModal = this.productElement.querySelector('product-modal');
			if (productModal) productModal.remove();

            // Prune editorial content only, on the detached copy. Never reconstruct the
            // purchase form: it owns recipient fields, add-ons, B2B and payment rules.
            // Most intros are marketing; only retain an explicit condition disclosure.
            // The legacy imperfect-packaging copy is rendered in this same intro slot.
            const identity = [this.productElement.querySelector('.product__title')?.textContent, this.productElement.querySelector('variant-selects, variant-radios')?.dataset.url].join(' ');
            const preserveCondition = /imperfect|damaged|packaging|scratch|dented|seconds/i.test(identity) || !/^en(?:-|$)/i.test(this.productLanguage || 'en');
            this.productElement.querySelectorAll('.stellar--intro, .stellar__lead').forEach(intro => {
                if (intro.closest('[data-stellar-quick-condition]')) return;
                const copy = intro.textContent.replace(/\s+/g, ' ');
                if (preserveCondition || intro.matches('[data-product-condition]') || /(?:imperfect|damaged|dented|scratched|missing).{0,60}packag|packag.{0,60}(?:imperfect|damaged|dented|scratched|missing)/i.test(copy)) {
                    intro.setAttribute('data-stellar-quick-condition', '');
                }
            });
            const editorial = [
                '.stellar--intro', '.stellar__lead', '.stellar-customer-highlight',
                '.product__description-fixed', '.main-product-description',
                '.stellar-purchase-benefits', '.stellar-product-guidance-slot',
                '.stellar-width-guidance', '.stellar-related-products',
                '.stellar-gallery-stories',
                '.product-about', '.share-buttons', '.product__tags',
                '.bls__visitors', '.bls__product-review-sold',
                '.product_blog-image', '[data-stellar-product-schema]'
            ];
            this.productElement.querySelectorAll(editorial.join(',')).forEach(element => {
                // Payment-method artwork uses the same image class as editorial blocks.
                if (element.closest('.stellar-payment-methods') || (element.matches('.product_blog-image') && element.closest('form, product-form, .stellar-purchase-price, .price-wrapper, .stellar-order-inclusions, .stellar-buyer-benefits'))) return;
                // A future content block containing a purchase control must be kept.
                if (!element.closest('[data-stellar-quick-condition]') && !element.querySelector('[data-stellar-quick-condition], form, product-form, input, select, textarea, [data-stellar-express-slot]')) element.remove();
            });
            const title = this.productElement.querySelector('.product__title');
            const rating = this.productElement.querySelector('.stellar-product-price-rating__reviews');
            if (title && rating) title.before(rating);
            const titleWrapper = title?.closest('.product__title__wrapper');
            const purchasePrice = this.productElement.querySelector('.stellar-purchase-price');
            if (titleWrapper && purchasePrice && !purchasePrice.contains(titleWrapper)) {
                // Move existing nodes, including native price IDs and observer script.
                const header = document.createElement('div');
                header.className = 'stellar-quick-product-header';
                titleWrapper.before(header);
                if (rating) header.append(rating);
                header.append(titleWrapper, purchasePrice);
            }
            this.productElement.querySelectorAll('form > .product-form__buttons').forEach(buttons => {
                if (buttons.querySelector('table') || buttons.closest('.b2b-product-form')) return;
                buttons.classList.add('stellar-quick-add-actions');
                // Keep every inclusion and its terms, but put buying controls first.
                const form = buttons.closest('form');
                form.querySelectorAll('.stellar-order-inclusions').forEach(inclusions => {
                    if (inclusions.querySelector('input, select, textarea, button, form')) return;
                    inclusions.classList.add('stellar-quick-inclusions-visible');
                    buttons.after(inclusions);
                    const heading = inclusions.querySelector('.stellar-order-inclusions__heading');
                    if (heading) {
                        heading.classList.add('stellar-quick-inclusions-heading');
                        inclusions.before(heading);
                    }
                });
            });
		}

		preventDuplicatedIDs() {
			const sectionId = this.productElement.dataset.section;
			this.productElement.innerHTML = this.productElement.innerHTML.replaceAll(sectionId, `quickadd-${ sectionId }`);
            const optionIds = new Set(['gift-wrap-checkbox', 'personalisation-checkbox', 'allergies', 'personalisationError', 'giftWrappingProduct', 'your-name', 'PopupModal-gift_cart', 'ProductPopup-gift_cart', 'ModalClose-gift_cart', 'PopupModal-personalisation_info', 'ProductPopup-personalisation_info', 'ModalClose-personalisation_info']);
            const scope = this.stellarOptionScope ||= `quickadd-options-${QuickAddModal.stellarOptionCount = (QuickAddModal.stellarOptionCount || 0) + 1}`;
            const ids = new Map();
            this.productElement.querySelectorAll('[id]').forEach(element => {
                if (!optionIds.has(element.id)) return;
                const original = element.id;
                if (original.startsWith('PopupModal-')) element.dataset.stellarPopup = original;
                element.id = `${original}-${scope}`; ids.set(original, element.id);
            });
            this.productElement.querySelectorAll('[for], [aria-describedby], [aria-labelledby], [aria-controls], [data-modal], a[href^="#"]').forEach(element => {
                for (const attribute of ['for', 'aria-describedby', 'aria-labelledby', 'aria-controls', 'data-modal', 'href']) {
                    const value = element.getAttribute(attribute);
                    if (!value) continue;
                    if (attribute === 'data-modal' || attribute === 'href') {
                        if (value.startsWith('#') && ids.has(value.slice(1))) element.setAttribute(attribute, '#' + ids.get(value.slice(1)));
                    } else element.setAttribute(attribute, value.split(/\s+/).map(id => ids.get(id) || id).join(' '));
                }
            });
            this.importedDialogIds = Array.from(this.productElement.querySelectorAll('modal-dialog'), (modal, index) => {
                if (!modal.id) modal.id = `${scope}-dialog-${index}`;
                modal.dataset.stellarQuickOwner = this.id;
                return modal.id;
            });
			this.productElement.querySelectorAll('variant-selects, variant-radios').forEach((variantSelect) => {
				variantSelect.dataset.originalSection = sectionId;
                variantSelect.dataset.updateUrl = 'false';
			});
		}

        cleanupImportedDialogs() {
            cancelAnimationFrame(this.importedDialogFocusFrame);
            this.importedDialogObserver?.disconnect();
            if (this.importedParentInert !== undefined) {
                this.inert = this.importedParentInert;
                this.importedParentInert = undefined;
            }
            (this.importedDialogs || []).forEach(modal => {
                if (modal.hasAttribute('open')) modal.hide();
                modal.remove();
            });
            this.importedDialogs = [];
        }

        bindImportedDialogs() {
            this.importedDialogs = (this.importedDialogIds || []).map(id => document.getElementById(id)).filter(Boolean);
            this.importedDialogObserver = new MutationObserver(records => {
                const openModal = this.importedDialogs.find(modal => modal.hasAttribute('open'));
                if (openModal) {
                    if (this.importedParentInert === undefined) this.importedParentInert = this.inert;
                    this.inert = true;
                    openModal.style.zIndex = String((parseInt(getComputedStyle(this).zIndex, 10) || 100) + 1);
                    cancelAnimationFrame(this.importedDialogFocusFrame);
                    this.importedDialogFocusFrame = requestAnimationFrame(() => {
                        this.importedDialogFocusFrame = requestAnimationFrame(() => {
                            if (!openModal.hasAttribute('open') || openModal.contains(document.activeElement)) return;
                            trapFocus(openModal, openModal.querySelector('[role="dialog"]'));
                        });
                    });
                    return;
                }
                if (this.importedParentInert === undefined) return;
                this.inert = this.importedParentInert;
                this.importedParentInert = undefined;
                const closed = records.find(record => record.oldValue !== null)?.target;
                cancelAnimationFrame(this.importedDialogFocusFrame);
                this.importedDialogFocusFrame = requestAnimationFrame(() => {
                    if (!this.hasAttribute('open') || this.importedDialogs.some(modal => modal.hasAttribute('open'))) return;
                    const target = closed?.openedBy;
                    trapFocus(this, target?.isConnected ? target : this.querySelector('[id^="ModalClose-"]'));
                });
            });
            this.importedDialogs.forEach(modal => {
                modal.querySelector('[role="dialog"]')?.setAttribute('tabindex', '-1');
                this.importedDialogObserver.observe(modal, { attributes: true, attributeOldValue: true, attributeFilter: ['open'] });
            });
        }

		preventVariantURLSwitching() {
			this.modalContent.querySelectorAll('variant-radios,variant-selects').forEach(picker => picker.setAttribute('data-update-url', 'false'));
		}

		removeGalleryListSemantic() {
			const galleryList = this.modalContent.querySelector('[id^="Slider-Gallery"]');
			if (!galleryList) return;

			galleryList.setAttribute('role', 'presentation');
			galleryList.querySelectorAll('[id^="Slide-"]').forEach(li => li.setAttribute('role', 'presentation'));
		}

		updateImageSizes() {
			const product = this.modalContent.querySelector('.product');
			if (!product) return;
			const desktopColumns = product.classList.contains('product--columns');
			if (!desktopColumns) return;

			const mediaImages = product.querySelectorAll('.product__media img');
			if (!mediaImages.length) return;

			let mediaImageSizes = '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

			if (product.classList.contains('product--medium')) {
				mediaImageSizes = mediaImageSizes.replace('715px', '605px');
			} else if (product.classList.contains('product--small')) {
				mediaImageSizes = mediaImageSizes.replace('715px', '495px');
			}

			mediaImages.forEach(img => img.setAttribute('sizes', mediaImageSizes));
		}
	});
}


if (!window.stellarQuickAddOverflowBound) {
  window.stellarQuickAddOverflowBound = true;
document.addEventListener("DOMContentLoaded", function() {
  // Get all elements with class "product__modal-opener"
  var modalOpeners = document.querySelectorAll(".custom_quick");

  // Function to add class "overflow-hidden" to <html> tag
  function addOverflowHidden() {
    document.documentElement.classList.add("overflow-hidden");
  }

  // Function to remove class "overflow-hidden" from <html> tag
  function removeOverflowHidden() {
    document.documentElement.classList.remove("overflow-hidden");
  }

  // Loop through each "product__modal-opener" element
  modalOpeners.forEach(function(opener) {
    // Add click event listener
    opener.addEventListener("click", function() {
      addOverflowHidden();
    });
  });

  // Get all elements with class "modal-close-button"
  var modalCloseButtons = document.querySelectorAll(".modal-close-button");

  // Loop through each "modal-close-button" element
  modalCloseButtons.forEach(function(button) {
    // Add click event listener
    button.addEventListener("click", function() {
      removeOverflowHidden();
    });
  });
});
}
